import * as React from "react";
import { Card, CardBody, PageContainer } from "../components/Card";
import { PageHeader } from "../components/PageHeader";
import { Button, IconButton } from "../components/Button";
import { Input, Select } from "../components/Form";
import { Table, Thead, Tbody, Tr, Th, Td, TdActions } from "../components/Table";
import { Badge } from "../components/Badge";
import { Banner, ListStates } from "../components/States";
import { IdBadge } from "../components/Detail";
import { useAsync } from "../general/useAsync";
import { AuditEntryModel, AuditPageModel } from "../api/APIClient";
import { auditClient } from "../api/clients";

/**
 * The audit trail.
 *
 * Every write that matters funnels through `AuditSaveChangesInterceptor`, so this is the record of
 * who changed what and when - including the `ManualMarkOverride` entries the calculation engine
 * writes when a run overwrites a hand-entered mark.
 *
 * Before and after are stored as raw JSON blobs. They are shown expanded on demand rather than
 * inline, because they are large and only occasionally the thing you need.
 */

const ACTION_TONES: Record<string, "success" | "info" | "warning" | "danger" | "neutral"> = {
    Created: "success",
    Updated: "info",
    Deleted: "danger",
    ManualMarkOverride: "warning",
    Finalized: "info",
    Unfinalized: "warning",
    Approved: "success",
    Rejected: "danger",
};

export const AuditIndex: React.FC = () => {
    const [entityType, setEntityType] = React.useState("");
    const [entityID, setEntityID] = React.useState("");
    const [action, setAction] = React.useState("");
    const [actorID, setActorID] = React.useState("");
    const [from, setFrom] = React.useState("");
    const [to, setTo] = React.useState("");
    const [page, setPage] = React.useState(1);
    const [expanded, setExpanded] = React.useState<string | null>(null);

    const pageSize = 50;

    /** The set of actions actually present, so the filter offers real values rather than guesses. */
    const actions = useAsync(() => auditClient.actions(), [], { initial: [] as string[] });

    const entries = useAsync<AuditPageModel>(
        () =>
            auditClient.query(
                entityType.trim() || undefined,
                entityID.trim() || undefined,
                action || undefined,
                actorID.trim() || undefined,
                from ? new Date(from) : undefined,
                // An end date means "up to the end of that day", not midnight at its start.
                to ? new Date(`${to}T23:59:59`) : undefined,
                page,
                pageSize
            ),
        [entityType, entityID, action, actorID, from, to, page],
        { debounceMs: 300 }
    );

    React.useEffect(() => setPage(1), [entityType, entityID, action, actorID, from, to]);

    const rows = entries.data?.entries ?? [];
    const totalPages = Math.max(1, Math.ceil((entries.data?.totalCount ?? 0) / (entries.data?.pageSize ?? pageSize)));
    const hasFilters = !!(entityType || entityID || action || actorID || from || to);

    return (
        <PageContainer>
            <PageHeader
                title="Audit trail"
                icon={["fad", "clock-rotate-left"]}
                description="Who changed what, and when."
                actions={
                    hasFilters && (
                        <Button
                            variant="ghost"
                            icon={["fad", "xmark"]}
                            onClick={() => {
                                setEntityType("");
                                setEntityID("");
                                setAction("");
                                setActorID("");
                                setFrom("");
                                setTo("");
                            }}
                        >
                            Clear filters
                        </Button>
                    )
                }
            />

            <Card className="mb-4">
                <CardBody className="p-4">
                    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                        <div>
                            <label className="mb-1 block text-sm font-medium text-text">Entity type</label>
                            <Input value={entityType} placeholder="e.g. MarkResult" onChange={(e) => setEntityType(e.target.value)} />
                        </div>
                        <div>
                            <label className="mb-1 block text-sm font-medium text-text">Entity id</label>
                            <Input value={entityID} placeholder="ULID" onChange={(e) => setEntityID(e.target.value)} />
                        </div>
                        <div>
                            <label className="mb-1 block text-sm font-medium text-text">Action</label>
                            <Select value={action} placeholder="Any action" onChange={(e) => setAction(e.target.value)}>
                                {(actions.data ?? []).map((a) => (
                                    <option key={a} value={a}>
                                        {a}
                                    </option>
                                ))}
                            </Select>
                        </div>
                        <div>
                            <label className="mb-1 block text-sm font-medium text-text">Actor id</label>
                            <Input value={actorID} placeholder="Identity-server user id" onChange={(e) => setActorID(e.target.value)} />
                        </div>
                        <div>
                            <label className="mb-1 block text-sm font-medium text-text">From</label>
                            <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
                        </div>
                        <div>
                            <label className="mb-1 block text-sm font-medium text-text">To</label>
                            <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
                        </div>
                    </div>
                </CardBody>
            </Card>

            <Card>
                <ListStates
                    isLoading={entries.isLoading}
                    error={entries.error}
                    onRetry={entries.refresh}
                    isEmpty={rows.length === 0}
                    empty={{
                        icon: ["fad", "clock-rotate-left"],
                        title: hasFilters ? "No audit entries match these filters" : "No audit entries yet",
                        hint: hasFilters ? "Try widening the date range or clearing a filter." : undefined,
                    }}
                >
                    <Table>
                        <Thead>
                            <Tr>
                                <Th>When</Th>
                                <Th>Action</Th>
                                <Th>Entity</Th>
                                <Th>Actor</Th>
                                <Th align="right">Detail</Th>
                            </Tr>
                        </Thead>
                        <Tbody>
                            {rows.map((entry) => (
                                <AuditRow
                                    key={entry.id}
                                    entry={entry}
                                    isExpanded={expanded === entry.id}
                                    onToggle={() => setExpanded(expanded === entry.id ? null : entry.id ?? null)}
                                />
                            ))}
                        </Tbody>
                    </Table>
                </ListStates>

                {totalPages > 1 && (
                    <CardBody className="flex items-center justify-between border-t border-border">
                        <span className="text-xs text-text-weak">
                            Page {entries.data?.page ?? page} of {totalPages} - {entries.data?.totalCount ?? 0} entries
                        </span>
                        <div className="flex gap-2">
                            <Button
                                size="sm"
                                variant="outline-secondary"
                                icon={["fad", "chevron-left"]}
                                disabled={page <= 1 || entries.isLoading}
                                onClick={() => setPage(page - 1)}
                            >
                                Previous
                            </Button>
                            <Button
                                size="sm"
                                variant="outline-secondary"
                                icon={["fad", "chevron-right"]}
                                disabled={page >= totalPages || entries.isLoading}
                                onClick={() => setPage(page + 1)}
                            >
                                Next
                            </Button>
                        </div>
                    </CardBody>
                )}
            </Card>

            <Banner tone="info" className="mt-4">
                Entries are written by an EF interceptor, so they cover every tracked write - including the overrides a calculation run
                makes to hand-entered marks.
            </Banner>
        </PageContainer>
    );
};

const AuditRow: React.FC<{ entry: AuditEntryModel; isExpanded: boolean; onToggle: () => void }> = ({ entry, isExpanded, onToggle }) => {
    const hasDetail = !!(entry.beforeJson || entry.afterJson);

    return (
        <>
            <Tr>
                <Td className="whitespace-nowrap">
                    {entry.occurredAt ? new Date(entry.occurredAt).toLocaleString() : <span className="text-text-weak">&mdash;</span>}
                </Td>
                <Td>
                    <Badge tone={ACTION_TONES[entry.action ?? ""] ?? "neutral"}>{entry.action}</Badge>
                </Td>
                <Td>
                    <span className="text-text-strong">{entry.entityType}</span>
                    <div>
                        <IdBadge id={entry.entityID} />
                    </div>
                </Td>
                <Td>
                    <IdBadge id={entry.actorID} />
                </Td>
                <TdActions>
                    {hasDetail && (
                        <IconButton
                            icon={["fad", isExpanded ? "chevron-up" : "chevron-down"]}
                            label={isExpanded ? "Hide detail" : "Show before and after"}
                            onClick={onToggle}
                        />
                    )}
                </TdActions>
            </Tr>

            {isExpanded && (
                <tr>
                    <td colSpan={5} className="bg-surface-muted px-6 py-4">
                        <div className="grid gap-4 lg:grid-cols-2">
                            <JsonBlock title="Before" json={entry.beforeJson} />
                            <JsonBlock title="After" json={entry.afterJson} />
                        </div>
                    </td>
                </tr>
            )}
        </>
    );
};

/** Pretty-prints when it can, falls back to the raw string when the blob is not valid JSON. */
const JsonBlock: React.FC<{ title: string; json?: string }> = ({ title, json }) => {
    const formatted = React.useMemo(() => {
        if (!json) return "";
        try {
            return JSON.stringify(JSON.parse(json), null, 2);
        } catch {
            return json;
        }
    }, [json]);

    return (
        <div className="min-w-0">
            <h4 className="mb-1 text-xs font-semibold uppercase tracking-wider text-text-weak">{title}</h4>
            {formatted ? (
                <pre className="max-h-80 overflow-auto rounded-md border border-border bg-surface p-3 font-mono text-xs text-text scrollbar-custom">
                    {formatted}
                </pre>
            ) : (
                <p className="text-sm text-text-weak">Nothing recorded.</p>
            )}
        </div>
    );
};

export default AuditIndex;
